import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react'
import { es } from '../i18n/es'
import { en } from '../i18n/en'
import type { Translations, AppLanguage } from '../i18n/types'

const STORAGE_KEY = 'cvtoday_app_language'

const translations: Record<AppLanguage, Translations> = { es, en }

interface LanguageContextValue {
  language: AppLanguage
  setLanguage: (lang: AppLanguage) => void
  t: Translations
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

function detectBrowserLanguage(): AppLanguage {
  if (typeof navigator === 'undefined') return 'es'
  const browserLang = navigator.language.split('-')[0]
  if (browserLang === 'en') return 'en'
  return 'es' // Default to Spanish
}

function getStoredLanguage(): AppLanguage | null {
  if (typeof window === 'undefined') return null
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'es' || stored === 'en') return stored
  return null
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(() => {
    return getStoredLanguage() || detectBrowserLanguage()
  })

  const setLanguage = useCallback((lang: AppLanguage) => {
    setLanguageState(lang)
    localStorage.setItem(STORAGE_KEY, lang)
  }, [])

  // Update document lang attribute
  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  const value: LanguageContextValue = {
    language,
    setLanguage,
    t: translations[language],
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useTranslation(): LanguageContextValue {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider')
  }
  return context
}
